import { Component, Host, h, JSX, State, Prop, Element } from '@stencil/core'
import { IRating, IVote } from '@rdss/client'

declare const _satellite: { track }

@Component({
    tag: 'zr-rating',
    styleUrl: 'zr-rating.scss',
    scoped: true,
})

/**
 * ZooRoyal ratings component.
 */
export class ZrRating {
    /**
     * The product-name to which the reviews relate.
     */
    @Prop() productName: string

    /**
     * Store the state of the component layer.
     */
    @State() isModalInView = false

    /**
     * Store the rating elements of the product.
     */
    @Prop() rating: IRating

    /**
     * Store the vote elements of the product.
     */
    @Prop() reviews: IVote[]

    /**
     * Store all possible ratings.
     */
    @Prop() possibleRatings = [5, 4, 3, 2, 1]

    /**
     * Store locale for decimal formatting.
     */
    @Prop() locale = 'de-DE'

    /**
     * Store for the selected rating star amount (e.g. "3").
     */
    @Prop({ mutable: true }) selectedRating: string

    /**
     * Store for the votes filtered by the selectedRating value.
     */
    @Prop({ mutable: true }) filteredVotes: IVote[]

    @Prop() labelStars = 'Sterne'
    @Prop() labelStarMultiplePossible = 'Stern(e)'
    @Prop() labelVotes = 'Bewertungen'
    @Prop() labelViewVotes = 'Bewertungen anzeigen'
    @Prop() disclaimer = 'Den Produktbewertungen liegen tatsächliche Einkäufe zugrunde.'

    @Element() host: HTMLZrRatingElement

    toLocaleNumberFormat = (value: number) => {
        return new Intl.NumberFormat(this.locale, { style: 'decimal', maximumSignificantDigits: 2 }).format(value)
    }

    toRoundedDecimal = (value: number) => {
        return Math.round(value)
    }

    toRoundedMultipleOf = (value: number, multipleOf = 5) => {
        return Math.ceil(value / multipleOf) * multipleOf
    }

    /**
     * Filter votes for star rating selection.
     */
    private filterVotes(): void {
        this.selectedRating = this.host.getElementsByTagName('select')[0].value

        this.filteredVotes = this.reviews.filter((vote) => {
            return vote.points?.toString() === this.selectedRating
        })
    }

    /**
     * Get vote distribution element.
     *
     * @returns {JSX.Element} The rendered vote distribution element.
     */
    private getVoteDistributionElement(): JSX.Element {
        return (
            this.getTransformedRatings().map(rating => (
                <zr-rating-diagram
                    points={rating.points}
                    percentage={rating.percentage}
                />
            ))
        )
    }

    /**
     * Transform ratings values to add percentages in object.
     *
     * @returns {{ 'total', 'points', 'percentage' }[]} Data enriched with percentage.
     */
    private getTransformedRatings(): { 'total', 'points', 'percentage' }[] {
        const totalVotes = this.rating.count
        let transformedData = []

        this.rating.pointCount.map((value) => {
            transformedData[value.points] = {
                'total': value.total,
                'points': value.points,
                'percentage': ((Number.parseInt(value.total, 10) * 100) / totalVotes).toFixed(0),
            }
        })

        for (let i = 1; i <= 5; i++) {
            const existingData = transformedData[i]

            if (typeof existingData === 'undefined') {
                transformedData[i] = {
                    'total': 0,
                    'points': i,
                    'percentage': 0,
                }
            }
        }

        transformedData = transformedData.sort((ratingA, ratingB) => {
            return ratingA.points > ratingB.points ? -1 : 1
        })

        return transformedData
    }

    /**
     * Renders the component.
     *
     * @returns {JSX.Element} - The rendered component.
     */
    render(): JSX.Element {
        return (
            <Host>
                <div class="zr-rating__header">
                    <p class="zr-rating__title">{this.labelVotes}</p>
                    <p class="zr-rating__count">({this.rating.count})</p>
                </div>

                <zr-rating-summary
                    average={this.toLocaleNumberFormat(this.rating.average / 2)}
                />

                <div class="zr-rating__distribution body rating-percentage-container">
                    {this.getVoteDistributionElement()}
                </div>

                <zr-modal>
                    <div slot="zr-modal-content">
                        <h2 class="zr-rating__modal-headline">
                            {this.labelVotes}
                        </h2>

                        <zr-rating-summary
                            average={this.toLocaleNumberFormat(this.rating.average / 2)}
                        />

                        <div class="zr-rating__distribution">
                            {this.getVoteDistributionElement()}
                        </div>

                        {(this.disclaimer?.length > 1) && (
                            <div class="zr-rating__disclaimer" innerHTML={this.disclaimer} />
                        )}

                        <div class="zr-rating__filter" >
                            <select
                                class="zr-rating__filter-select"
                                name="zr-rating__filter"
                                onChange={() => this.filterVotes()}>
                                {this.possibleRatings.sort((n1, n2) => n2 - n1).map(rating => (
                                    <option value={rating}>{rating} {this.labelStarMultiplePossible}</option>
                                ))}
                            </select>

                            <p>
                                {this.filteredVotes?.length} {this.labelVotes}
                            </p>
                        </div>

                        <div class="zr-rating__review-wrapper">
                            {this.filteredVotes?.map(review => (
                                <div class="zr-rating__review">
                                    <div class="zr-rating__review-header">
                                        <p class="zr-rating__review-author">
                                            {review.name}
                                        </p>
                                        <p class="zr-rating__review-date">
                                            {(new Date(review.createdAt.date)).toLocaleDateString(this.locale)}
                                        </p>
                                    </div>
                                    <p class="zr-rating__review-content">
                                        {review.comment}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div slot="zr-modal-trigger">
                        <button
                            class="zr-rating__button"
                            onClick={() => {
                                this.filterVotes()
                                _satellite?.track('AdHocEvent', 'click_reviews-details')
                            }}
                            title={this.labelViewVotes}>
                            {this.labelViewVotes}
                        </button>
                    </div>
                </zr-modal>
            </Host>
        )
    }
}
